/**
 * Auto Clamp Builder <-> Shopify cart bridge.
 * Isolated asset. Does not modify any global cart/product/layout file.
 * Talks to the builder iframe over postMessage only, with strict origin + source validation
 * on every message in both directions.
 */
(function () {
  "use strict";

  var MESSAGE_TYPE_ADD_TO_CART = "AUTO_CLAMP_BUILDER_ADD_TO_CART";
  var MESSAGE_TYPE_HEIGHT_CHANGED = "AUTO_CLAMP_BUILDER_HEIGHT_CHANGED";
  var MESSAGE_TYPE_HEIGHT_REQUEST = "AUTO_CLAMP_BUILDER_HEIGHT_REQUEST";
  var ACK_TYPES = {
    started: "AUTO_CLAMP_BUILDER_CART_ADD_STARTED",
    succeeded: "AUTO_CLAMP_BUILDER_CART_ADD_SUCCEEDED",
    failed: "AUTO_CLAMP_BUILDER_CART_ADD_FAILED"
  };

  // Documented safe range for the auto-height protocol. Values outside it are ignored
  // outright, so a compromised or buggy frame can neither collapse the embed to nothing nor
  // inflate the page to an absurd height.
  var MIN_HEIGHT_PX = 600;
  var MAX_HEIGHT_PX = 5000;

  var CONFIGURATION_ID_PATTERN = /^ACB-[0-9A-F]{16}$/;
  var CORRELATION_ID_PATTERN = /^acb-cart-[0-9a-f]{32}$/;
  var NUMERIC_ID_PATTERN = /^[1-9][0-9]*$/;

  var MAX_CORRELATION_ID_LEN = 64;
  var MAX_VARIANT_ID_LEN = 20;
  var MAX_CONFIGURATION_ID_LEN = 32;
  var MAX_SUMMARY_LEN = 500;
  var MAX_QUOTE_FIELD_LEN = 512;
  var MAX_HARDWARE_LEN = 200;

  // The Hardware line is a customer-facing, per-bracket bill of materials authored entirely by
  // the builder. This bridge never computes any part of it: it does not derive bolt lengths, it
  // never multiplies anything by quantity, and it never substitutes a quote-time total. Its only
  // job is to prove the string is exactly the documented per-bracket form, then pass it through
  // verbatim.
  //
  // The "Per bracket: " prefix is load-bearing, not decoration. It is what makes a quote-time
  // total such as "4 x bolts" fail closed, and it is why the text stays per-bracket no matter
  // what quantity is ordered.
  //
  // The character class is a strict allowlist rather than a denylist, so HTML (< > & " '),
  // newlines and anything else unexpected are rejected by construction instead of by trying to
  // enumerate every dangerous byte. The leading [A-Za-z0-9] forbids a prefix followed by only
  // punctuation or spaces.
  var HARDWARE_PATTERN = /^Per bracket: [A-Za-z0-9][A-Za-z0-9 .,;()\/×-]*$/;

  var frame = document.getElementById("AutoClampBuilderFrame");
  var statusEl = document.getElementById("AutoClampBuilderStatus");
  if (!frame) return;

  // The single trusted origin, derived once from the iframe's configured src. Deriving it here
  // rather than hard-coding a literal keeps this bridge bound to exactly whichever builder
  // deployment the section is configured to embed (production or an immutable preview), while
  // remaining an exact, single-origin match - never a wildcard, never a suffix/prefix test, and
  // never re-derived from an incoming message. If src is missing or unparseable, no origin can
  // be trusted, so the bridge stays inert and no message is ever accepted.
  var BUILDER_ORIGIN = (function () {
    try {
      return new URL(frame.getAttribute("src"), window.location.href).origin;
    } catch (e) {
      return null;
    }
  })();
  if (!BUILDER_ORIGIN || BUILDER_ORIGIN === "null" || BUILDER_ORIGIN === window.location.origin) return;

  // The trusted frame-window reference is never re-derived from an incoming message event.
  // contentWindow is a stable reference to the iframe's browsing context that exists as soon
  // as the iframe element itself exists in the DOM - it does not require the "load" event to
  // have fired. Assigning it immediately at init avoids a race where the iframe (e.g. already
  // cached) fires "load" before this script has attached its listener, which would otherwise
  // leave frameWindow null indefinitely. The "load" listener is retained to refresh the
  // reference after any subsequent navigation/reload of the iframe.
  // The "load" listener that refreshes this reference is registered at the bottom of this file,
  // together with the height-request handshake, so that the parent's own message listener is
  // always attached before any request is sent.
  var frameWindow = frame.contentWindow;

  // correlationId -> "in-flight" | "done", so a duplicate or overlapping message for the
  // same attempt can never be processed twice, concurrently or otherwise.
  var correlationState = Object.create(null);

  // Tracks a downward height change (shrink) that was deliberately not fully applied because
  // doing so would have clamped the reader's scroll position and jumped the whole page. The
  // remainder is completed later, from the scroll handler, once it can be reclaimed without
  // moving the page. null means nothing is deferred.
  var pendingShrinkTargetPx = null;

  function setStatus(state, text) {
    if (!statusEl) return;
    if (!text) {
      statusEl.hidden = true;
      statusEl.textContent = "";
      statusEl.removeAttribute("data-state");
      return;
    }
    statusEl.hidden = false;
    statusEl.setAttribute("data-state", state);
    statusEl.textContent = text;
  }

  function postAck(type, correlationId, message) {
    if (!frameWindow) return;
    var payload = { type: type, correlationId: correlationId };
    if (message) payload.message = message;
    // Target origin is always the exact builder origin - never "*".
    frameWindow.postMessage(payload, BUILDER_ORIGIN);
  }

  function isBoundedString(value, maxLen) {
    return typeof value === "string" && value.length > 0 && value.length <= maxLen;
  }

  // Rejects C0 (0-31) and C1 (127-159) control characters. Checked explicitly by code point
  // rather than folded into HARDWARE_PATTERN because JavaScript's "$" anchor also matches
  // immediately before a single trailing newline, so a value ending in one would otherwise
  // satisfy the pattern despite the allowlist.
  function hasControlCharacters(value) {
    for (var i = 0; i < value.length; i++) {
      var code = value.charCodeAt(i);
      if (code < 32 || (code >= 127 && code <= 159)) return true;
    }
    return false;
  }

  function isValidHardware(value) {
    if (!isBoundedString(value, MAX_HARDWARE_LEN)) return false;
    if (hasControlCharacters(value)) return false;
    return HARDWARE_PATTERN.test(value);
  }

  // ---------------------------------------------------------------------------
  // Auto-height receiver.
  //
  // Origin and source are already proven by the listener below before this is reached, so this
  // function's job is solely to prove the PAYLOAD is exactly the documented height message and
  // nothing else. The shape is deliberately closed rather than open: exactly two own keys, so a
  // message carrying an extra "selector", "css", "target", "style" or similar field is rejected
  // outright instead of being partially honoured. Nothing from the payload is ever interpolated
  // into a selector, a style string, or any CSS beyond a single numeric px height, and the only
  // element ever touched is this bridge's own iframe - looked up by id at init, never named by
  // the message - so a message cannot address any other iframe or element on the page.
  // ---------------------------------------------------------------------------
  function isHeightMessage(data) {
    // Plain object only: rejects null, arrays, strings, numbers and exotic/host objects.
    if (Object.prototype.toString.call(data) !== "[object Object]") return false;
    return data.type === MESSAGE_TYPE_HEIGHT_CHANGED;
  }

  function isAcceptableHeightPayload(data) {
    var keys = Object.keys(data);
    // Exactly two own keys, and exactly the two documented ones. Rejects both missing and
    // extra fields (including any injection-style field) in one check.
    if (keys.length !== 2) return false;
    if (keys.indexOf("type") === -1 || keys.indexOf("heightPx") === -1) return false;
    if (data.type !== MESSAGE_TYPE_HEIGHT_CHANGED) return false;

    var height = data.heightPx;
    // Number.isInteger is false for strings ("900"), NaN, Infinity/-Infinity and fractions,
    // so this single check covers every non-finite / non-integer case the contract excludes.
    if (typeof height !== "number" || !Number.isInteger(height)) return false;
    if (height < MIN_HEIGHT_PX || height > MAX_HEIGHT_PX) return false;
    return true;
  }

  // Returns true when the message was recognised as a height message (valid or not), so the
  // caller stops processing it. Invalid height messages are ignored silently - never logged,
  // never echoed back, and never allowed to fall through to the cart path.
  //
  // Deliberately NEVER sends a height request from here. The request is emitted only from the
  // two lifecycle points below (init and genuine iframe load), so a height response can never
  // trigger another request and no request/response loop can form, however many responses the
  // builder sends.
  function handleHeightMessage(data) {
    if (!isHeightMessage(data)) return false;
    if (isAcceptableHeightPayload(data)) {
      // The only mutation: this iframe's own inline height. Width, scrolling, classes,
      // attributes, selectors and every other element are left untouched. Applied through
      // applyFrameHeight so a transient shrink can never yank the parent page's scroll.
      applyFrameHeight(data.heightPx);
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Scroll-stable height application.
  //
  // The only place frame.style.height is ever assigned. Growing the embed is always safe: it
  // extends the document downward and never moves what the reader is looking at. Shrinking is
  // not: when the reader is scrolled near the bottom of the page, reducing the document height
  // pushes the maximum scroll offset below the current one and the browser clamps scrollY,
  // visibly jumping the whole page - and it does NOT restore the position when the height grows
  // back. The builder produces exactly this pattern on every edit, briefly dropping to a shorter
  // "updating price" layout during its async price fetch and then restoring to the settled one.
  //
  // So a shrink is only applied as far as the slack that exists below the current viewport; any
  // remainder is remembered and completed later from the scroll handler, where the space can be
  // reclaimed without moving the page. Every assignment is still a single bounded numeric px
  // value between the builder's already-validated target and the current applied height - never a
  // selector, a style string or anything else. Auto-height still fully settles, shrinks included;
  // it is only deferred, never disabled, and the frame is never given an inner scrollbar or a
  // fixed fallback height.
  // ---------------------------------------------------------------------------
  function applyFrameHeight(targetPx) {
    var currentPx = parseInt(frame.style.height, 10);
    var docEl = document.documentElement;
    var viewportH = window.innerHeight || 0;
    var docH = docEl ? docEl.scrollHeight : 0;

    // Grow/unchanged, the first real height (frame still on its CSS fallback, no inline height),
    // or no laid-out document to reason about (e.g. a non-visual environment): apply directly.
    // There is no scroll position that a downward move could clamp.
    if (!isFinite(currentPx) || currentPx <= 0 || targetPx >= currentPx || !viewportH || docH <= 0) {
      frame.style.height = targetPx + "px";
      pendingShrinkTargetPx = null;
      return;
    }

    // Shrink with a real layout: cap it to the slack below the current viewport so the document
    // never becomes shorter than (scrollTop + viewport) and the browser cannot clamp the scroll.
    var scrollTop = window.pageYOffset || docEl.scrollTop || 0;
    var maxShrink = Math.floor(Math.max(0, docH - viewportH - scrollTop));
    var shrinkBy = currentPx - targetPx;
    if (shrinkBy <= maxShrink) {
      frame.style.height = targetPx + "px";
      pendingShrinkTargetPx = null;
    } else {
      frame.style.height = currentPx - maxShrink + "px";
      pendingShrinkTargetPx = targetPx;
    }
  }

  // Completes a previously-deferred shrink as the reader scrolls up and the space below the
  // viewport reappears. Reads layout only; the sole mutation is the same bounded px height.
  function reclaimDeferredShrink() {
    if (pendingShrinkTargetPx === null) return;
    var targetPx = pendingShrinkTargetPx;
    var currentPx = parseInt(frame.style.height, 10);
    if (!isFinite(currentPx) || targetPx >= currentPx) {
      pendingShrinkTargetPx = null;
      return;
    }
    var docEl = document.documentElement;
    var viewportH = window.innerHeight || 0;
    var docH = docEl ? docEl.scrollHeight : 0;
    if (!viewportH || docH <= 0) return;
    var scrollTop = window.pageYOffset || docEl.scrollTop || 0;
    var maxShrink = Math.floor(Math.max(0, docH - viewportH - scrollTop));
    var shrinkBy = currentPx - targetPx;
    if (shrinkBy <= maxShrink) {
      frame.style.height = targetPx + "px";
      pendingShrinkTargetPx = null;
    } else if (maxShrink > 0) {
      frame.style.height = currentPx - maxShrink + "px";
    }
  }

  // Asks the builder to report its current content height.
  //
  // Why this exists: the builder reports its height on its own initiative, but a fast or cached
  // iframe can finish and report before this script has attached its message listener, and the
  // builder's own change-detection then suppresses an identical repeat - leaving the embed stuck
  // on the CSS fallback height forever. This request lets the parent explicitly re-ask, so that
  // race is recoverable rather than terminal.
  //
  // The request carries the type and nothing else: no selectors, configuration, price, quote or
  // cart data of any kind. It is always targeted at the exact builder origin, never "*".
  function sendHeightRequest() {
    if (!frameWindow) return;
    frameWindow.postMessage({ type: MESSAGE_TYPE_HEIGHT_REQUEST }, BUILDER_ORIGIN);
  }

  function validateMessage(data) {
    if (!data || typeof data !== "object") return null;
    if (data.type !== MESSAGE_TYPE_ADD_TO_CART) return null;

    var errors = [];
    var correlationIdOk = isBoundedString(data.correlationId, MAX_CORRELATION_ID_LEN) && CORRELATION_ID_PATTERN.test(data.correlationId);
    if (!correlationIdOk) errors.push("correlationId");

    if (!isBoundedString(data.variantIdNumeric, MAX_VARIANT_ID_LEN) || !NUMERIC_ID_PATTERN.test(data.variantIdNumeric)) errors.push("variantIdNumeric");
    if (!Number.isInteger(data.quantity) || data.quantity < 1 || data.quantity > 50) errors.push("quantity");
    if (!isBoundedString(data.configurationId, MAX_CONFIGURATION_ID_LEN) || !CONFIGURATION_ID_PATTERN.test(data.configurationId)) errors.push("configurationId");
    if (!isBoundedString(data.bracketSummary, MAX_SUMMARY_LEN)) errors.push("bracketSummary");
    // Fails closed: a missing, empty, malformed, overlong, control-character-bearing or
    // non-per-bracket Hardware value pushes an error here, so handleAddToCart is never reached
    // and no /cart/add request is issued.
    if (!isValidHardware(data.hardware)) errors.push("hardware");
    if (!isBoundedString(data.quoteReference, MAX_QUOTE_FIELD_LEN)) errors.push("quoteReference");
    if (!isBoundedString(data.quoteToken, MAX_QUOTE_FIELD_LEN)) errors.push("quoteToken");
    if (!Number.isInteger(data.unitPriceCents) || data.unitPriceCents <= 0) errors.push("unitPriceCents");
    if (!Number.isInteger(data.totalPriceCents) || data.totalPriceCents <= 0) errors.push("totalPriceCents");
    if (
      Number.isInteger(data.unitPriceCents) &&
      Number.isInteger(data.quantity) &&
      Number.isInteger(data.totalPriceCents) &&
      data.totalPriceCents !== data.unitPriceCents * data.quantity
    ) {
      errors.push("totalPriceCents-mismatch");
    }

    // The properties object itself is validated, not merely trusted: exactly the five
    // documented keys, each matching the already-validated top-level field it mirrors.
    var props = data.properties;
    if (!props || typeof props !== "object") {
      errors.push("properties");
    } else {
      var expectedKeys = ["Configuration ID", "Bracket Summary", "Hardware", "_auto_clamp_quote_reference", "_auto_clamp_quote_token"];
      var actualKeys = Object.keys(props);
      var keysMatchExactly =
        actualKeys.length === expectedKeys.length &&
        expectedKeys.every(function (k) {
          return actualKeys.indexOf(k) !== -1;
        });
      if (!keysMatchExactly) {
        errors.push("properties-unexpected-keys");
      } else {
        if (props["Configuration ID"] !== data.configurationId) errors.push("properties.Configuration ID mismatch");
        if (props["Bracket Summary"] !== data.bracketSummary) errors.push("properties.Bracket Summary mismatch");
        if (props["Hardware"] !== data.hardware) errors.push("properties.Hardware mismatch");
        if (props["_auto_clamp_quote_reference"] !== data.quoteReference) errors.push("properties._auto_clamp_quote_reference mismatch");
        if (props["_auto_clamp_quote_token"] !== data.quoteToken) errors.push("properties._auto_clamp_quote_token mismatch");
      }
    }

    if (errors.length) {
      return { ok: false, errors: errors, correlationId: correlationIdOk ? data.correlationId : null };
    }
    return { ok: true, data: data };
  }

  // Never forward the caller-supplied properties object as-is. Rebuild it from the
  // individually validated fields only, so nothing unexpected can ever be injected.
  function buildTrustedProperties(data) {
    var props = {};
    props["Configuration ID"] = data.configurationId;
    props["Bracket Summary"] = data.bracketSummary;
    // Passed through exactly as validated - never recomputed, never scaled by data.quantity.
    props["Hardware"] = data.hardware;
    props["_auto_clamp_quote_reference"] = data.quoteReference;
    props["_auto_clamp_quote_token"] = data.quoteToken;
    return props;
  }

  function getCartDrawer() {
    return document.querySelector("cart-drawer");
  }

  // Shopify's /cart/add response carries a "status" field on EVERY response, success or
  // failure (e.g. 200 on success, 422 on failure) - it is not an error flag by itself, and
  // must never be treated as one merely because it is truthy. Only a genuine error signal
  // (non-2xx HTTP, a numeric status >= 400, or an explicit errors/description field) means
  // the add failed.
  function isCartAddErrorResponse(result) {
    if (!result.ok) return true;
    var json = result.json || {};
    var numericStatus = typeof json.status === "number" ? json.status : null;
    if (numericStatus !== null && numericStatus >= 400) return true;
    if (json.errors) return true;
    if (typeof json.description === "string" && json.description.length > 0) return true;
    return false;
  }

  // Best-effort presentation only. Cart-add success has already been established and
  // acknowledged before this runs - nothing here can turn a success into a failure, and
  // nothing here may issue another /cart/add request.
  function showSuccessWithCartLink(cartUrl) {
    if (!statusEl) return;
    statusEl.hidden = false;
    statusEl.setAttribute("data-state", "success");
    statusEl.textContent = "";
    var text = document.createTextNode("Added to cart. ");
    var link = document.createElement("a");
    link.href = cartUrl;
    link.textContent = "View cart";
    statusEl.appendChild(text);
    statusEl.appendChild(link);
  }

  // A dedicated error subtype for a response Shopify itself explicitly rejected (e.g. a
  // genuine 4xx with an error body). This is NOT ambiguous - Shopify told us plainly that
  // nothing was added, so no reconciliation is needed and FAILED is sent directly.
  function ConfirmedCartAddRejection() {}
  ConfirmedCartAddRejection.prototype = Object.create(Error.prototype);

  // Reconciliation: used only when the outcome of the one and only /cart/add request is
  // ambiguous (its response could not be parsed, or an unexpected exception occurred after
  // it was dispatched) - never as a way to retry or issue a second cart mutation. This is a
  // single read-only GET, matched against the unique data of the original request. Quote
  // reference/token values are compared in memory only - never logged or displayed.
  function reconcileCartState(data, correlationId, cartUrl) {
    var cartJsUrl = cartUrl.replace(/\/$/, "") + ".js";
    fetch(cartJsUrl, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var matches = items.filter(function (item) {
          return (
            item.id === parseInt(data.variantIdNumeric, 10) &&
            item.quantity === data.quantity &&
            item.properties &&
            item.properties["_auto_clamp_quote_reference"] === data.quoteReference &&
            item.properties["Configuration ID"] === data.configurationId
          );
        });

        if (matches.length === 1) {
          setStatus("success", "Added to cart.");
          postAck(ACK_TYPES.succeeded, correlationId);
          showSuccessWithCartLink(cartUrl);
        } else if (matches.length === 0) {
          setStatus("error", "We couldn’t add that to your cart. Please try again.");
          postAck(ACK_TYPES.failed, correlationId, "Could not add item to cart.");
        } else {
          // More than one matching line - genuinely ambiguous. Never guess, never add or
          // remove anything here; surface a safe, non-committal state for manual review.
          setStatus("error", "We couldn’t confirm your cart. Please check your cart before continuing.");
          postAck(ACK_TYPES.failed, correlationId, "Could not confirm cart status.");
        }
        correlationState[correlationId] = "done";
      })
      .catch(function () {
        // Even the read-only reconciliation check failed - never assume success without
        // evidence. Fall back to the same safe FAILED outcome.
        setStatus("error", "We couldn’t add that to your cart. Please try again.");
        postAck(ACK_TYPES.failed, correlationId, "Could not add item to cart.");
        correlationState[correlationId] = "done";
      });
  }

  function handleAddToCart(validated) {
    var data = validated.data;
    var correlationId = data.correlationId;

    if (correlationState[correlationId]) {
      // Already being processed, or already finished - ignore any duplicate/concurrent message.
      return;
    }
    correlationState[correlationId] = "in-flight";

    setStatus("loading", "Adding your bracket to cart…");
    postAck(ACK_TYPES.started, correlationId);

    var cartDrawer = getCartDrawer();
    var sectionsToRender =
      cartDrawer && typeof cartDrawer.getSectionsToRender === "function" ? cartDrawer.getSectionsToRender() : null;

    var body = {
      items: [
        {
          id: parseInt(data.variantIdNumeric, 10),
          quantity: data.quantity,
          properties: buildTrustedProperties(data)
        }
      ]
    };
    if (sectionsToRender) {
      body.sections = sectionsToRender.map(function (section) {
        return section.section || section.id;
      });
      body.sections_url = window.location.pathname;
    }

    var cartAddUrl = (window.routes && window.routes.cart_add_url) || "/cart/add.js";
    var cartUrl = (window.routes && window.routes.cart_url) || "/cart";

    // Exactly one /cart/add request is issued for this attempt, no matter what happens to
    // its response below - ambiguous outcomes are resolved by a read-only GET /cart.js
    // reconciliation, never by repeating this call.
    fetch(cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, json: json };
        });
        // If response.json() throws (unparseable body), that rejection is NOT caught here -
        // it propagates to the ambiguous branch below, exactly like any other unexpected
        // exception occurring after the request was dispatched.
      })
      .then(function (result) {
        if (isCartAddErrorResponse(result)) {
          throw new ConfirmedCartAddRejection();
        }

        // Cart-add success is established here, independently of whether a drawer exists
        // or can render. The acknowledgement is sent now and is final for this attempt.
        setStatus("success", "Added to cart.");
        postAck(ACK_TYPES.succeeded, correlationId);
        correlationState[correlationId] = "done";

        // Drawer presentation is best-effort only, after success is already confirmed and
        // acknowledged. A missing drawer or a thrown renderContents() falls back to a safe,
        // non-mutating link - it never re-issues the cart request and never reverses the ack.
        try {
          if (cartDrawer && typeof cartDrawer.renderContents === "function") {
            cartDrawer.renderContents(result.json);
          } else {
            showSuccessWithCartLink(cartUrl);
          }
        } catch (renderError) {
          showSuccessWithCartLink(cartUrl);
        }
      })
      .catch(function (err) {
        if (err instanceof ConfirmedCartAddRejection) {
          // Shopify explicitly rejected the request - a confirmed, non-ambiguous outcome.
          // No reconciliation needed; nothing was added.
          setStatus("error", "We couldn’t add that to your cart. Please try again.");
          postAck(ACK_TYPES.failed, correlationId, "Could not add item to cart.");
          correlationState[correlationId] = "done";
          return;
        }
        // Ambiguous: the response couldn't be parsed, the network failed after the request
        // was dispatched, or some other unexpected exception occurred. We genuinely don't
        // know whether Shopify committed the addition, so we check - via a single read-only
        // GET - rather than guess, and never issue a second /cart/add to find out.
        reconcileCartState(data, correlationId, cartUrl);
      });
  }

  window.addEventListener("message", function (event) {
    if (event.origin !== BUILDER_ORIGIN) return;
    if (!frameWindow || event.source !== frameWindow) return;

    // Height messages are consumed here, after origin/source are proven and before any cart
    // handling, so they can never reach or disturb the cart path.
    if (handleHeightMessage(event.data)) return;

    var validated = validateMessage(event.data);
    if (!validated) return; // Not our message type, or too malformed to identify - ignore silently.

    if (!validated.ok) {
      if (validated.correlationId && !correlationState[validated.correlationId]) {
        correlationState[validated.correlationId] = "done";
        postAck(ACK_TYPES.failed, validated.correlationId, "Invalid add-to-cart request.");
      }
      return;
    }

    handleAddToCart(validated);
  });

  // ---------------------------------------------------------------------------
  // Height handshake lifecycle. Ordering below is deliberate and load-bearing:
  //
  //   1. The window "message" listener above is already attached by this point, so a builder
  //      that answers instantly (cached, same-tick) can never out-race the parent's listener.
  //   2. The iframe "load" listener is registered next, refreshing the trusted frame-window
  //      reference and re-asking on every genuine load (initial load, reload, re-navigation).
  //   3. Only then is the first request sent, covering the case where the iframe had already
  //      finished loading before this script ran - "load" has then already fired and will not
  //      fire again, so without this init-time request that state would never recover.
  //
  // Requests are emitted from these two points only, never from a height response, so there is
  // no request/response loop.
  // ---------------------------------------------------------------------------
  frame.addEventListener("load", function () {
    frameWindow = frame.contentWindow;
    sendHeightRequest();
  });

  // Reclaim any deferred shrink as the reader scrolls up. Passive and cheap: it returns
  // immediately unless a shrink is actually pending, and the only element it can ever touch is
  // this bridge's own iframe height.
  window.addEventListener("scroll", reclaimDeferredShrink, { passive: true });

  sendHeightRequest();
})();
